import { useFormikContext } from "formik"
import { Box, Grid, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { FormikDatePicker, FormikSwitch } from "../../../../../shared/components/forms/Formik"
import { MatchEnvironment } from "../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../environment/envTypes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"

import { getProductGroupTimeInterval, getProductGroupWeekDays } from "../productGroupAvailability"
import { CreateUpdateProductFormValues } from "../productsTypes"
import { TimeIntervalField } from "./TimeIntervalField"
import { WeekDaysField } from "./WeekDaysField"

const useStyles = makeStyles()((theme) => ({
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "0.875rem 1fr",
        background: theme.palette.tailwind.sky[50],
        borderRadius: "0.5rem",
        gap: "0.75rem",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[700],
        width: "100%",
    },
}))

// The backend does not store weekDays and timeInterval on products yet, so they only show where they are being tested
const SCHEDULE_ENVIRONMENTS = [EnvironmentType.Local, EnvironmentType.Test]

type AvailabilityDetailsFieldProps = Readonly<{}>

export function AvailabilityDetailsField({}: AvailabilityDetailsFieldProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { values, errors } = useFormikContext<CreateUpdateProductFormValues>()
    const groupAvailability = values.productGroup?.availabilityDetails

    return (
        <>
            <LocalizedStrict id="product-create-availability-from-date-picker" attrs={{ label: true }}>
                <FormikDatePicker
                    name="availabilityDetails.dateInterval.from"
                    range="all"
                    label="From date"
                    slotProps={{
                        field: { clearable: true },
                    }}
                />
            </LocalizedStrict>
            <LocalizedStrict id="product-create-availability-to-date-picker" attrs={{ label: true }}>
                <FormikDatePicker
                    name="availabilityDetails.dateInterval.to"
                    range="all"
                    label="To date"
                    slotProps={{
                        field: { clearable: true },
                    }}
                />
            </LocalizedStrict>
            {typeof errors.availabilityDetails === "string" && (
                <Typography variant="body2" color="error">
                    {errors.availabilityDetails}
                </Typography>
            )}
            <MatchEnvironment environments={SCHEDULE_ENVIRONMENTS}>
                <WeekDaysField groupWeekDays={getProductGroupWeekDays(groupAvailability)} />
                <TimeIntervalField
                    groupTimeInterval={getProductGroupTimeInterval(groupAvailability, dateTimeFactory)}
                />
            </MatchEnvironment>
            <Grid item>
                <LocalizedStrict id="product-create-availability-preorder-switch">
                    <FormikSwitch name="availabilityDetails.allowPreOrder">Disallow pre-order:</FormikSwitch>
                </LocalizedStrict>
            </Grid>
            <Box className={classes.infoWrap}>
                <FontAwesomeIcon icon={faCircleInfo} />
                <LocalizedStrict id="product-create-availability-info">
                    <Typography variant="body2">
                        Customers can pre-order products for future dates in the GoPay app unless you disallow it here.
                    </Typography>
                </LocalizedStrict>
            </Box>
        </>
    )
}
